import React from 'react';
import { FileText, Edit, CheckCircle, Clock, FileCheck, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { valuationProcess } from '../data/mock';
import SEO from '@/components/SEO';

const Process = () => {
  const iconMap = {
    FileText, Edit, CheckCircle
  };

  const requiredDocuments = [
    {
      title: "Ownership Proof",
      items: ["Property Tax Bill", "Land Record (7/12 Extract)", "Sale Deed", "Property Card"]
    },
    {
      title: "Owner Identification",
      items: ["Aadhar Card", "PAN Card"]
    },
    {
      title: "Additional Documents (if applicable)",
      items: ["Building Plan Approval", "Occupancy Certificate", "NOC from Society", "Previous Valuation Reports"]
    }
  ];

  return (
    <div className="bg-background pt-32 pb-20">
      <SEO
        title="Valuation Process — Mehta Associates"
        description="Simple, transparent valuation process — submit documents, get draft, receive final government-approved report. Fast 24-hour delivery options available."
        pathname="/process"
        keywords={["valuation process", "how property valuation works", "Mehta Associates"]}
      />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="heading-1 text-foreground mb-6">VALUATION PROCESS</h1>
          <p className="body-large text-[#666666] max-w-3xl mx-auto">
            Get your government-approved valuation report in just 3 simple steps. Fast, accurate, and hassle-free process.
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-20">
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-border"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {valuationProcess.map((step, index) => {
                const Icon = iconMap[step.icon];
                return (
                  <div key={step.step} className="relative">
                        {/* Step Number Circle */}
                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4 lg:translate-y-0">
                          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center border-4 border-border relative z-10">
                            <span className="text-2xl font-bold text-foreground">{step.step}</span>
                          </div>
                        </div>

                    {/* Card */}
                    <div className="bg-card border border-border p-8 pt-16 hover:border-primary transition-all duration-300 mt-8">
                      <div className="text-center mb-6">
                        <Icon className="text-foreground mx-auto mb-4" size={40} />
                        <h3 className="heading-5 text-foreground mb-3">{step.title}</h3>
                        <p className="body-small text-muted">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Timeline Details */}
        <div className="bg-card p-12 border border-border mb-20">
          <h2 className="heading-4 text-foreground mb-8 text-center">Complete Process Timeline</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Clock className="text-foreground mx-auto mb-3" size={32} />
              <div className="text-2xl font-bold text-foreground mb-2">0-1 Hr</div>
              <p className="caption text-muted">Document Submission & Verification</p>
            </div>
            <div className="text-center">
              <FileCheck className="text-foreground mx-auto mb-3" size={32} />
              <div className="text-2xl font-bold text-foreground mb-2">1-2 Hrs</div>
              <p className="caption text-muted">Property Analysis & Draft Preparation</p>
            </div>
            <div className="text-center">
              <Edit className="text-foreground mx-auto mb-3" size={32} />
              <div className="text-2xl font-bold text-foreground mb-2">24FF Hrs</div>
              <p className="caption text-muted">Review & Corrections (if needed)</p>
            </div>
            <div className="text-center">
              <Send className="text-foreground mx-auto mb-3" size={32} />
              <div className="text-2xl font-bold text-foreground mb-2">Same Day</div>
              <p className="caption text-muted">Final Report & Courier Dispatch</p>
            </div>
          </div>
        </div>

        {/* Required Documents */}
        <div className="mb-20">
          <h2 className="heading-4 text-foreground mb-8 text-center">Required Documents</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {requiredDocuments.map((doc, index) => (
              <div key={index} className="bg-card p-8 border border-border">
                <h3 className="heading-6 text-foreground mb-6">{doc.title}</h3>
                <ul className="space-y-3">
                  {doc.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="text-foreground flex-shrink-0 mt-1" size={18} />
                      <span className="body-small text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What You'll Receive */}
        <div className="bg-card p-12 border border-border mb-12">
          <h2 className="heading-4 text-foreground mb-8 text-center">What You'll Receive</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-foreground" size={20} />
              </div>
              <div>
                <h4 className="body-medium text-foreground mb-2">Government-Approved Certificate</h4>
                <p className="body-small text-muted">Signed and sealed by registered valuer with official stamp</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-foreground" size={20} />
              </div>
              <div>
                <h4 className="body-medium text-foreground mb-2">Detailed Valuation Report</h4>
                <p className="body-small text-muted">Complete market analysis and calculation methodology</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-foreground" size={20} />
              </div>
              <div>
                <h4 className="body-medium text-foreground mb-2">Instant Scan Copy</h4>
                <p className="body-small text-muted">Digital PDF version sent immediately via email</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-foreground" size={20} />
              </div>
              <div>
                <h4 className="body-medium text-foreground mb-2">Physical Copy via Courier</h4>
                <p className="body-small text-muted">Original hard copy dispatched same day to your address</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-card p-12 border-2 border-border">
          <h2 className="heading-4 text-foreground mb-4">Ready to Get Started?</h2>
          <p className="body-medium text-muted mb-8 max-w-2xl mx-auto">
            Submit your inquiry today and receive your valuation report within 24 hours
          </p>
          <Link to="/contact" className="btn-primary">
            Start Valuation Process
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Process;
